/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
// eslint-disable-next-line simple-import-sort/imports
import React, {useEffect, useMemo, useState} from 'react';
import {I18nextProvider} from 'react-i18next';
import {QueryClientProvider} from 'react-query';
import {ThemeProvider} from '@material-ui/core/styles';
import PageViewportMeta from '@ion/applications/src/components/PageViewportMeta';
import {isCrossEnvironment} from '@ion/applications/src/env/env';
import EnvironmentNotification from '@ion/applications/src/env/EnvironmentNotification';
import useRouteInitializer from '@ion/applications/src/hooks/useRouteInitializer';
import {ApplicationPage} from '@ion/applications/src/layout/types';
import {NetworkStatusPrompt} from '@ion/applications/src/prompts/NetworkStatusPrompt';
import {getFeatureFlags} from '@ion/applications/src/routes/RouteUtils';
import StoreProvider from '@ion/applications/src/store/StoreProvider';
import {launchReduxDevTools} from '@ion/applications/src/store/tools';
import {includeDatadog, isDevMode} from '@ion/applications/src/utils/env';
import Datadog from '@ion/core/src/analytics/Datadog';
import Pendo from '@ion/core/src/analytics/Pendo';
import {get, getEnvironment} from '@ion/core/src/environment/environment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import i18n, {initializeLanguage, localeHMR} from '@ion/core/src/locale/locale';
import {enable, info} from '@ion/core/src/logger/logger';
import {setConnectConfig} from '@ion/core/src/services/ConnectUtils';
import Sisense from '@ion/core/src/services/Sisense';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {registerGrid} from '@ion/dls/src/grid/IonGridLicence';
import {setStaticGridConfig} from '@ion/dls/src/grid/useStaticGridConfig';
import {ErrorBoundary} from '@ion/dls/src/notifications/ErrorBoundary';
import {getQueryClient} from '@ion/query/src/config/client';
import {DevTools} from '@ion/query/src/tools/DevTools';
import {setGraphQLConfig} from '@ion/services/src/graphql/GraphQLConfig';
import {setPreAuthConfig} from '@ion/services/src/websocket/PreAuth';
import {initializeRouter, isDebug} from '@/routes/RouteDefinitions';
import rootReducer from '@/store/reducers';
import loadConfig from '../env/env';
import Layout from '../layout/Layout';
import FeatureFlags from '../security/FeatureFlags';
import {
  getAPMEnablement,
  initializeAPM
} from '../telemetry/apm';
import '@ion/dls/src/fonts/fonts.scss';
import theme from '../theme/theme';
import '@ion/applications/src/styles/index.scss';
import 'react-phone-number-input/style.css';
import 'filepond/dist/filepond.min.css';

const isDev = isDevMode();

// matches the header ADM's GraphQL and Connect calls send ...
const ADM_APP_KEY_HEADER = 'calamp-services-app';

/**
 * Defines the root iOn Application, this component wraps all routes and injects
 * all providers, including i18Next and Material UI.
 */
function CTCAdmin({Component, pageProps}: ApplicationPage) {
  const [initialized, setInitialized]       = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const layoutProps = useMemo(() => Component.getLayoutProps?.() || {}, [Component]);

  const isMounted  = useHasMounted();

  // Used for compatibility between App Router and Pages Router
  useRouteInitializer();

  useEffect(() => {
    FeatureFlags.applyFeatureFlags(getFeatureFlags('features'));
  }, []);

  useEffect(() => {
    setStaticGridConfig({rowBuffer: 50});

    async function initialize() {
      setIsInitializing(true);
      const {enableAPMFramework, enableAPMDebugOnly} = getAPMEnablement();
      enable(isDebug() || isDev || enableAPMDebugOnly);
      initializeRouter();
      info('Initializing Application ...');
      initializeLanguage();

      const config = await loadConfig();
      if (!isDev || (enableAPMFramework && !enableAPMDebugOnly)) {
        Pendo.injectScript(config.pendoAPIKey, globalThis);
      }
      // datadog is included wherever it is enabled and the environment
      // configuration supplies the values, and only if not running against a
      // different environment via the `environment=` query param, which would
      // otherwise initialize against that environment's datadog application ...
      if (!isDev && includeDatadog() && !isCrossEnvironment()) {
        Datadog.initialize({
          service : 'ion-ctc-admin',
          env     : getEnvironment(),
          version : process.env.NEXT_PUBLIC_APP_VERSION
        });
      }
      setGraphQLConfig({
        serviceURL : get('graphQLServiceURL'),
        appKey     : get('appKey')
      });
      setPreAuthConfig({...config, appKeyHeader: ADM_APP_KEY_HEADER});
      initializeAPM();
      Sisense.injectFrameScript();
      setConnectConfig(config);
      setInitialized(true);
      registerGrid(config);
      isDev && localeHMR();
      isDev && launchReduxDevTools();
    }
    if (isMounted && !initialized && !isInitializing) {
      initialize();
    }
  }, [isMounted, initialized, isInitializing]);

  return (
    <>
      <PageViewportMeta />
      {initialized && (
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={getQueryClient()}>
              <ErrorBoundary>
                <StoreProvider rootReducer={rootReducer}>
                  {!layoutProps.omit ? (
                    <Layout {...layoutProps}>
                      <Component {...pageProps} />
                    </Layout>
                  ) : (
                    <Component {...pageProps}/>
                  )}
                </StoreProvider>
              </ErrorBoundary>
              <DevTools />
            </QueryClientProvider>
          </ThemeProvider>
        </I18nextProvider>
      )}
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          {isMounted && !initialized && (
            <ContentLoader width='60px' height='60px' fullPage />
          )}
          <NetworkStatusPrompt />
          <EnvironmentNotification app='CTC Admin' />
        </ThemeProvider>
      </I18nextProvider>
    </>
  );
};
export default CTCAdmin;
